import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  ButtonGroup,
  AppBar,
  Toolbar,
  Drawer,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsIcon from "@mui/icons-material/Notifications";
import TableChartIcon from "@mui/icons-material/TableChart";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

import NotificationGroups from "./notificationGroups";
import EventPartyDefaults from "./EventPartyDefaults";
import PhoneSearch from "./PhoneSearch";
import EmailSearch from "./EmailSearch";
import NotificationGroupDetailed from "./notificationGroupDetailed";
import { useLocation, useNavigate } from "react-router-dom";
import { MenuIcon } from "lucide-react";

export default function Notifications() {
  const location = useLocation();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState(() => {
    const path = location.pathname;
    if (path.includes("/notification-group")) return "notification-group";
    if (path.includes("/events-party")) return "events-party";
    if (path.includes("/by-phone")) return "by-phone";
    if (path.includes("/by-email")) return "by-email";
    return "notification-group";
  });

  const [phoneSearchDialog, setPhoneSearchDialog] = useState(false);
  const [emailSearchDialog, setEmailSearchDialog] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [searchedPhoneNumber, setSearchedPhoneNumber] = useState(null);
  const [searchedEmailAddress, setSearchedEmailAddress] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  // Simple key to force remount
  const [componentKey, setComponentKey] = useState(0);

  // burger button
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/notification-group")) setViewMode("notification-group");
    else if (path.includes("/events-party")) setViewMode("events-party");
    else if (path.includes("/by-phone")) setViewMode("by-phone");
    else if (path.includes("/by-email")) setViewMode("by-email");
  }, [location.pathname]);

  const handleViewChange = (newView) => {
    if (newView === "by-phone") {
      if (viewMode === "by-phone") {
        // Already on this view, just refresh
        setPhoneSearchDialog(true);
      } else {
        setPhoneSearchDialog(true);
      }
    } else if (newView === "by-email") {
      if (viewMode === "by-email") {
        // Already on this view, just refresh
        setEmailSearchDialog(true);
      } else {
        setEmailSearchDialog(true);
      }
    } else if (newView === "events-party") {
      if (viewMode === "events-party") {
        // Already on this view, just refresh
        setComponentKey((prev) => prev + 1);
      } else {
        // Navigate up one level (..) then to events-party
        navigate("../notifications/events-party", { replace: true });
      }
    } else {
      // notification-group
      if (viewMode === "notification-group") {
        // Already on this view, just refresh
        setComponentKey((prev) => prev + 1);
      } else {
        // Navigate up one level (..) then to notification-group
        navigate("../notifications/notification-group", { replace: true });
      }
    }
  };

  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
    setViewMode("groupdetails");
  };

  const handleBackToGroups = () => {
    setViewMode("notification-group");
    setSelectedGroupId(null);
    navigate("../notifications/notification-group", { replace: true });
  };

  const handlePhoneSearchSubmit = () => {
    if (phoneNumber.trim()) {
      setSearchedPhoneNumber(phoneNumber.trim());
      setPhoneSearchDialog(false);
      // Navigate up one level (..) then to by-phone
      navigate("../notifications/by-phone/" + phoneNumber.trim(), {
        replace: true,
      });
      setComponentKey((prev) => prev + 1);
    }
  };

  const handlePhoneSearchCancel = () => {
    setPhoneSearchDialog(false);
    setPhoneNumber("");
  };

  const handleEmailSearchSubmit = () => {
    if (emailAddress.trim()) {
      setSearchedEmailAddress(emailAddress.trim());
      setEmailSearchDialog(false);
      // Navigate up one level (..) then to by-email
      navigate("../notifications/by-email/" + emailAddress.trim(), {
        replace: true,
      });
      setComponentKey((prev) => prev + 1);
    }
  };

  const handleEmailSearchCancel = () => {
    setEmailSearchDialog(false);
    setEmailAddress("");
  };

  const renderCurrentView = () => {
    switch (viewMode) {
      case "notification-group":
        return (
          <NotificationGroups
            key={`notification-group-${componentKey}`}
            onGroupSelect={handleGroupSelect}
          />
        );
      case "events-party":
        return <EventPartyDefaults key={`events-party-${componentKey}`} />;
      case "by-phone":
        return <PhoneSearch key={`by-phone-${componentKey}`} />;
      case "by-email":
        return <EmailSearch key={`by-email-${componentKey}`} />;
      case "groupdetails":
        return (
          <NotificationGroupDetailed
            groupId={selectedGroupId}
            onBack={handleBackToGroups}
          />
        );
      default:
        return (
          <NotificationGroups
            key={`notification-group-${componentKey}`}
            onGroupSelect={handleGroupSelect}
          />
        );
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      {/* Navigation Bar */}
      <AppBar
        position="static"
        sx={{
          boxShadow: 0,
          display: { xs: "none", sm: "block" },
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "right",
            py: 1,
            gap: 2,
            bgcolor: "transparent",
          }}
        >
          <ButtonGroup
            sx={{
              boxShadow: "none",
              "& .MuiButton-root": {
                borderRadius: 0,
                height: 48,
                boxShadow: "none",
                outline: "none",
              },
            }}
          >
            <Button
              startIcon={<NotificationsIcon />}
              onClick={() => handleViewChange("notification-group")}
              sx={{
                px: { xs: 1, sm: 3, md: 3 },
                py: 0,
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "10px", sm: "14px", md: "14px" },
                minWidth: "160px",
                bgcolor:
                  viewMode === "notification-group" ? "#1565c0" : "#374151",
                color: "white",
                "&:hover": {
                  bgcolor:
                    viewMode === "notification-group" ? "#1565c0" : "#4b5563",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              Notification Groups
            </Button>

            <Button
              startIcon={<TableChartIcon />}
              onClick={() => handleViewChange("events-party")}
              sx={{
                px: { xs: 1, sm: 3, md: 3 },
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "10px", sm: "14px", md: "14px" },
                minWidth: "160px",
                bgcolor: viewMode === "events-party" ? "#1565c0" : "#374151",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "events-party" ? "#1565c0" : "#4b5563",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              Events Party
            </Button>

            <Button
              startIcon={<PhoneIcon />}
              onClick={() => handleViewChange("by-phone")}
              sx={{
                px: { xs: 1, sm: 3, md: 3 },
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "10px", sm: "14px", md: "14px" },
                minWidth: "160px",
                bgcolor: viewMode === "by-phone" ? "#1565c0" : "#374151",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "by-phone" ? "#1565c0" : "#4b5563",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              search by phone
            </Button>

            <Button
              startIcon={<EmailIcon />}
              onClick={() => handleViewChange("by-email")}
              sx={{
                px: { xs: 1, sm: 3, md: 3 },
                py: 1,
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "10px", sm: "14px", md: "14px" },
                minWidth: "160px",
                bgcolor: viewMode === "by-email" ? "#1565c0" : "#374151",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "by-email" ? "#1565c0" : "#4b5563",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              search by email
            </Button>
          </ButtonGroup>
        </Toolbar>
      </AppBar>

      {/* Mobile: Burger Menu */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          justifyContent: "flex-end",
          alignItems: "center",
          position: "absolute",
          top:4,
          right: 4,
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={() => setDrawerOpen(true)}
          sx={{
            color: "white",
            bgcolor: "#374151",
            "&:hover": { bgcolor: "#4b5563" },
            borderRadius: 1,
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: 280,
            bgcolor: "#1f2937",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Menu
            </Typography>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              fullWidth
              startIcon={<NotificationsIcon />}
              onClick={() => {
                handleViewChange("notification-group");
                setDrawerOpen(false);
              }}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                bgcolor: viewMode === "notification-group" ? "#1565c0" : "transparent",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "notification-group" ? "#1565c0" : "#374151",
                },
              }}
            >
              Notification Groups
            </Button>

            <Button
              fullWidth
              startIcon={<TableChartIcon />}
              onClick={() => {
                handleViewChange("events-party");
                setDrawerOpen(false);
              }}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                bgcolor:
                  viewMode === "events-party" ? "#1565c0" : "transparent",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "events-party" ? "#1565c0" : "#374151",
                },
              }}
            >
              Events Party
            </Button>

            <Button
              fullWidth
              startIcon={<PhoneIcon />}
              onClick={() => {
                handleViewChange("by-phone");
                setDrawerOpen(false);
              }}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                bgcolor: viewMode === "by-phone" ? "#1565c0" : "transparent",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "by-phone" ? "#1565c0" : "#374151",
                },
              }}
            >
              search by phone
            </Button>

            <Button
              fullWidth
              startIcon={<EmailIcon />}
              onClick={() => {
                handleViewChange("by-email");
                setDrawerOpen(false);
              }}
              sx={{
                justifyContent: "flex-start",
                px: 2,
                py: 1.5,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "14px",
                bgcolor: viewMode === "by-email" ? "#1565c0" : "transparent",
                color: "white",
                "&:hover": {
                  bgcolor: viewMode === "by-email" ? "#1565c0" : "#374151",
                },
              }}
            >
              search by email
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Content Area */}
      <Box>{renderCurrentView()}</Box>

      {/* Phone Search Dialog */}
      <Dialog
        open={phoneSearchDialog}
        onClose={handlePhoneSearchCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search by Phone Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Phone Number"
            placeholder="Enter phone number (e.g., 0123456789)"
            type="tel"
            fullWidth
            variant="outlined"
            value={phoneNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setPhoneNumber(value);
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData("Text").replace(/\D/g, "");
              setPhoneNumber((prev) => (prev + pasted).slice(0, 10));
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") handlePhoneSearchSubmit();
            }}
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              borderRadius: 0, // ✅ remove rounding
            }}
            onClick={handlePhoneSearchCancel}
            color="#fff"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePhoneSearchSubmit}
            variant="contained"
            color="primary"
            disabled={!phoneNumber.trim()}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Search Dialog */}
      <Dialog
        open={emailSearchDialog}
        onClose={handleEmailSearchCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Search by Email Address</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            placeholder="Enter email address (e.g., user@example.com)"
            type="email"
            fullWidth
            variant="outlined"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleEmailSearchSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            sx={{
              borderRadius: 0, // ✅ remove rounding
            }}
            onClick={handleEmailSearchCancel}
            color="#fff"
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailSearchSubmit}
            variant="contained"
            color="primary"
            disabled={!emailAddress.trim()}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
